import React, { useMemo, useState } from "react";
import { Button, Checkbox, Typography } from "@mui/material";
import { Feature } from "geojson";

import { getPathTypeCounts, getPreferenceCounts, getUniformPreference } from "./helper";
import PreferenceSelector from "./PreferenceSelector";

import { CheckboxRow, Container, Footer, Section } from "./SelectedPaths.styles";

interface SelectedPathsProps {
  features: Feature[];
  onCancel: () => void;
  onApply: (value: string, includeCrossings: boolean) => void;
}

const SelectedPaths: React.FC<SelectedPathsProps> = ({ features, onCancel, onApply }) => {
  const counts = useMemo(() => getPreferenceCounts(features), [features]);
  const uniformPreference = useMemo(() => getUniformPreference(counts), [counts]);
  const { crossings, paths } = getPathTypeCounts(features);
  const [includeCrossings, setIncludeCrossings] = useState(() => {
    return crossings > 0 && paths === 0;
  });
  const selected = includeCrossings ? paths + crossings : paths;

  const [value, setValue] = useState<string | null>(null);
  const activeValue = value ?? uniformPreference;

  return (
    <Container>
      <PreferenceSelector label="Selected paths" features={features} value={value} onChange={setValue} />

      <Section>
        <CheckboxRow>
          <Checkbox disableRipple size="small" checked={includeCrossings} onChange={() => setIncludeCrossings((prev) => !prev)} sx={{ p: 0 }} />
          <div>
            <Typography variant="body2" fontWeight={500}>
              Include crossings
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {crossings} crossing{crossings !== 1 ? "s" : ""} {includeCrossings ? "will be updated" : "kept as-is"}
            </Typography>
          </div>
        </CheckboxRow>
      </Section>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {selected} feature{selected !== 1 ? "s" : ""} will be adjusted when you apply
      </Typography>

      <Footer>
        <Button color="primary" size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={{ ml: 1, px: 2 }}
          disabled={activeValue === null}
          onClick={() => activeValue && onApply(activeValue, includeCrossings)}
        >
          Apply to {selected} item{selected !== 1 ? "s" : ""}
        </Button>
      </Footer>
    </Container>
  );
};

export default SelectedPaths;
